import React, { useMemo } from 'react';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AddCircleRounded,
  AutoAwesome,
  BookmarkRounded,
  ExpandLess,
  ExpandMore,
  Refresh,
  ReportProblemOutlined,
  WarningAmberRounded
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { AppContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { TILE_COLOURS } from './tileColours';
import { detectCrossEvidence } from '../../pedigree-tool/analysis/detectors/crossEvidence';
import { molecularSupport } from '../../pedigree-tool/analysis/molecularSupport';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import useGenccGrades from './useGenccGrades';
import { pairGrade, genesToGrade } from './genccRowGrade';
import { GENE_VALIDITY_STATE } from './genotypes-menu/geneValidity';
import { SIDEBAR_VALUE } from '../sidebarValues';
import HpoReleaseNote from 'src/components/UI/HpoReleaseNote';

function Suggestions({
  data,
  setDataset,
  analysis,
  reportSummary,
  // Is CLINICAL INSIGHTS open? Defaults TRUE so every existing caller and all
  // 24 tests behave exactly as before; only the sidebar passes the real value.
  // The default is the safe direction: forgetting to pass it costs performance,
  // never correctness.
  active = true
}) {
  const { setSnack } = React.useContext(SnackContext);
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  let user_id = useMemo(() => {
    return user?.user_id || user?._id;
  }, [user]);

  // Id for family member
  let familyMemberId = useMemo(() => {
    return data?.familyMemberId;
  }, [data]);

  // Id for proband
  let proBandId = useMemo(() => {
    return data?.proBandId;
  }, [data]);

  const [fetchData, setFetchData] = React.useState({
    hpoTerms: [],
    excludedHpoTerms: [],
    Genes: [],
    Diagnoses: []
  });
  // True while the per-node /genotypes /diagnoses /phenotypes fetch is in
  // flight. Used to distinguish "node has no clinical data" (show empty-state
  // CTA) from "fetch hasn't completed yet" (show spinner). Starts true so we
  // don't flash the CTA on first mount.
  const [fetchLoading, setFetchLoading] = React.useState(true);
  // CS-6 — set when the phenotypes endpoint could not enrich every stored term
  // from the external HPO API. The phenotypes themselves are all present (the
  // backend no longer drops the ones it could not enrich), so the ranking is
  // complete; only the descriptions are. Surfaced because the clinician is
  // otherwise looking at a panel whose term labels came from the stored record
  // rather than the live ontology, with no way to tell.
  const [enrichmentDegraded, setEnrichmentDegraded] = React.useState(null);

  // Single source of truth for how each Profile Match `type` maps to the
  // patient record: which fetchData key holds the patient's current items,
  // which AppContext event to dispatch on save, which backend endpoint and
  // payload shape to use, and the user-facing label.
  // `sendFullList` reflects each endpoint's contract — Genes' /genotypes
  // overwrites the whole array; Diagnoses/Phenotypes append-and-dedupe.
  const TYPE_MAP = useMemo(
    () => ({
      Genes: {
        fetchKey: 'Genes',
        event: EVENTS.SET_PEDIGREE_GENOTYPES,
        apiConstant: 'genotypes',
        payloadKey: 'genotypes',
        label: 'Genotypes',
        sendFullList: true,
        storeAsId: false
      },
      Diagnoses: {
        fetchKey: 'Diagnoses',
        event: EVENTS.SET_PEDIGREE_DIAGNOSES,
        apiConstant: 'diagnoses',
        payloadKey: 'diagnoses',
        label: 'Diagnoses',
        sendFullList: false,
        storeAsId: false
      },
      Diseases: {
        fetchKey: 'Diagnoses', // diseases are stored under Diagnoses
        event: EVENTS.SET_PEDIGREE_DIAGNOSES,
        apiConstant: 'diagnoses',
        payloadKey: 'diagnoses',
        label: 'Diseases',
        sendFullList: false,
        storeAsId: false
      },
      Phenotypes: {
        fetchKey: 'hpoTerms', // patient phenotypes live here as bare HP ids
        event: EVENTS.SET_PEDIGREE_PHENOTYPES,
        apiConstant: 'phenotypes',
        payloadKey: 'phenotypes',
        label: 'Phenotypes',
        sendFullList: false,
        storeAsId: true
      }
    }),
    []
  );

  const addSuggestion = (type, value) => {
    if (!type || !value) return;
    const cfg = TYPE_MAP[type];
    if (!cfg) return;

    const currentValues = fetchData[cfg.fetchKey] || [];
    const exists = cfg.storeAsId
      ? currentValues.includes(value.id)
      : currentValues.some((item) => item?.id === value.id);

    if (exists) {
      setSnack({
        status: true,
        msg: 'Already added!',
        severity: 'error'
      });
      return;
    }

    // Optimistic update so the bookmark icon flips immediately and the row
    // becomes inert. The authoritative refresh comes from getHPOTerm after
    // the dispatch below.
    const optimisticItem = cfg.storeAsId
      ? value.id
      : { id: value.id, name: value.name };
    const newValues = [...currentValues, optimisticItem];
    setFetchData((prev) => ({
      ...prev,
      [cfg.fetchKey]: newValues
    }));

    const id = proBandId ?? familyMemberId;
    const payload = {
      familyMemberId: id,
      user_id: user_id,
      isProband: proBandId ? true : false,
      ...(cfg.sendFullList
        ? { [cfg.payloadKey]: newValues }
        : { [cfg.payloadKey]: [{ id: value.id, name: value.name }] })
    };

    axios
      .post(APICONSTANTS[cfg.apiConstant], payload)
      .then((res) => {
        if (!res.data?.success) return;

        // Family-wide accumulator dispatch (fires the Profile Match
        // fingerprints and the per-node refetch in this component).
        dispatchUpdate({
          type: cfg.event,
          value: [{ id: value.id, name: value.name }]
        });

        // The Diseases / Phenotypes / Genotypes sidebar menus watch
        // users.selectedUser.<list> / users.family[i].<list>, not the
        // family-wide accumulator, so the SET_PEDIGREE_* event above is
        // not enough to refresh them. Push the new item into the affected
        // member's record so the menus' existing useEffects fire.
        const isProband = !!proBandId;
        const memberData = isProband
          ? users?.selectedUser
          : (users?.family || []).find(
              (m) => m.familyMemberId === familyMemberId
            );
        if (memberData) {
          const recordListKey =
            type === 'Phenotypes'
              ? 'phenotypes'
              : type === 'Genes'
              ? 'genotypes'
              : 'diagnoses';
          const existingList = memberData[recordListKey] || [];
          if (!existingList.some((it) => it?.id === value.id)) {
            const updatedList = [
              ...existingList,
              { id: value.id, name: value.name }
            ];
            dispatchUpdate({
              type: EVENTS.UPDATE_PEDIGREE_SELECTED_USER,
              value: isProband
                ? { ...memberData, [recordListKey]: updatedList }
                : { _id: memberData._id, [recordListKey]: updatedList }
            });
          }
        }

        // Keep the pedigree dataset (diagnoses chips on the node label)
        // in sync for the Diseases/Diagnoses path.
        if (
          (type === 'Diagnoses' || type === 'Diseases') &&
          res.data.allDiagnoses
        ) {
          setDataset((prev) =>
            prev.map((elem) =>
              elem.pid === data?.data?.pid
                ? { ...elem, diagnoses: res.data.allDiagnoses }
                : elem
            )
          );
        }

        setSnack({
          status: true,
          msg: `Added to ${cfg.label}`,
          severity: 'success'
        });
      })
      .catch(() => {
        // Roll back the optimistic update on failure so the bookmark icon
        // doesn't lie about persisted state.
        setFetchData((prev) => ({
          ...prev,
          [cfg.fetchKey]: currentValues
        }));
        setSnack({
          status: true,
          msg: 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  const getHPOTerm = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) {
      setFetchLoading(false);
      return;
    }

    setFetchLoading(true);
    Promise.all([
      axios.get(
        APICONSTANTS.getGenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      ),
      axios.get(
        APICONSTANTS.getDiagnosesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      ),
      axios.get(
        APICONSTANTS.getPhenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      )
    ])
      .then(([geno_res, diagno_res, pheno_res]) => {
        const phenotypes = pheno_res?.data?.phenotypes || [];
        const enrichment = pheno_res?.data?.enrichment;
        setEnrichmentDegraded(
          enrichment?.degraded
            ? { failed: enrichment.failed, total: enrichment.total }
            : null
        );
        setFetchData({
          Genes: geno_res?.data.genotypes?.map((gene) => ({
            id: gene.id,
            name: gene.name
          })),
          Diagnoses: diagno_res.data.diagnoses.map((dig) => ({
            id: dig.id,
            name: dig.name
          })),
          hpoTerms: phenotypes
            .filter((hp) => hp.observed !== false)
            .map((hp) => hp.id),
          excludedHpoTerms: phenotypes
            .filter((hp) => hp.observed === false)
            .map((hp) => hp.id)
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: 'Error fetching Values',
          severity: 'error'
        });
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };

  // Fingerprint of the family-wide phenotype set in AppContext. We don't
  // use this as the hpoIds source — `pedigreeSelectedUsers.phenotypes`
  // accumulates phenotypes across the whole selected pedigree, not just the
  // selected node — but a change to it is a reliable signal that *some*
  // phenotype was added/removed, so we re-run getHPOTerm to fetch the
  // current node's per-person list.
  const phenotypeFingerprint = useMemo(() => {
    const phens = users?.phenotypes;
    if (!Array.isArray(phens)) return '';
    return phens
      .map((p) => p?.id)
      .filter(Boolean)
      .sort()
      .join(',');
  }, [users]);

  // Same change-signal pattern for diseases: the AppContext-level
  // diagnoses accumulator may be family-wide, but a change to it means
  // *something* changed in the disease list and we should re-fetch.
  const diagnosesFingerprint = useMemo(() => {
    const dx = users?.diagnoses;
    if (!Array.isArray(dx)) return '';
    return dx
      .map((d) => d?.id)
      .filter(Boolean)
      .sort()
      .join(',');
  }, [users]);

  React.useEffect(() => {
    getHPOTerm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    proBandId,
    familyMemberId,
    user_id,
    phenotypeFingerprint,
    diagnosesFingerprint
  ]);

  // diseaseIds passed to the Profile Match panel. fetchData.Diagnoses
  // already comes from the per-person /diagnoses endpoint so the IDs are
  // correctly scoped to the selected node.
  const diseaseIds = useMemo(() => {
    const dx = fetchData.Diagnoses;
    if (!Array.isArray(dx)) return [];
    return dx.map((d) => d?.id).filter((id) => typeof id === 'string');
  }, [fetchData.Diagnoses]);

  const localHasInputs = fetchData.hpoTerms.length > 0 || diseaseIds.length > 0;

  // TGCLOUD-1669 Phase 1 — the collapsed-row summary, and the reason the
  // summary contract is three-valued rather than two.
  //
  // This section resolves in TWO request waves: /genotypes /diagnoses
  // /phenotypes populate `fetchData`, and only then does ProfileMatchBox POST
  // /terminology/profile-search for the ranked matches. Reporting "none
  // recorded" between the waves would assert a clinical absence that is not
  // true, so nothing is claimed until `fetchLoading` clears.
  //
  // What is summarised is the INPUTS, not the ranked disease the mock-up shows.
  // The ranking lives inside ProfileMatchBox behind the second wave; surfacing
  // it would mean lifting through another component and waiting on that wave.
  // Flagged for Kathryn at review.
  React.useEffect(() => {
    if (!reportSummary) return;
    if (fetchLoading) {
      reportSummary(SIDEBAR_VALUE.suggestions, { status: 'pending' });
      return;
    }
    reportSummary(SIDEBAR_VALUE.suggestions, {
      status: 'ready',
      phenotypeCount: fetchData.hpoTerms.length,
      diseaseCount: diseaseIds.length
    });
  }, [
    reportSummary,
    fetchLoading,
    fetchData.hpoTerms.length,
    diseaseIds.length
  ]);

  // ID sets of the patient's currently-recorded items, keyed by the same
  // `type` strings the Profile Match panel uses. Lets each row flag itself
  // as already-in-record (renders a bookmark instead of the + action).
  // Phenotypes include both present and absent — both are "already on the
  // record" from the suggestion-picker's perspective.
  const existingIds = useMemo(
    () => ({
      Diseases: new Set(
        (fetchData.Diagnoses || []).map((d) => d?.id).filter(Boolean)
      ),
      Genes: new Set((fetchData.Genes || []).map((g) => g?.id).filter(Boolean)),
      Phenotypes: new Set([
        ...(fetchData.hpoTerms || []),
        ...(fetchData.excludedHpoTerms || [])
      ])
    }),
    [
      fetchData.Diagnoses,
      fetchData.Genes,
      fetchData.hpoTerms,
      fetchData.excludedHpoTerms
    ]
  );

  // Patient's gene symbols (normalised) for gene↔disease convergence on the
  // disease cards, e.g. "BRCA1 (NCBIGene:672)" → "BRCA1".
  const patientGeneSet = useMemo(
    () =>
      new Set(
        (fetchData.Genes || [])
          .map((g) => (g?.name || '').split(' (')[0].trim().toUpperCase())
          .filter(Boolean)
      ),
    [fetchData.Genes]
  );

  // Genes the selected individual carries a PATHOGENIC variant in (from the
  // analyzer's per-individual output). Gates the Phase C molecular-support
  // re-rank so a benign/VUS held gene doesn't boost a disease.
  const patientPathogenicGenes = useMemo(
    () =>
      new Set(
        analysis?.individuals?.[String(data?.pid)]?.pathogenicGenes || []
      ),
    [analysis, data]
  );

  // TGCLOUD-1712 — the Phase C molecular-support re-rank is OFF unless a
  // deployment has explicitly opted in. Clinical Insights is registered as a
  // phenotype-driven ranking, so genotype must not reorder its output by
  // default. `useVAFeatureFlags` returns null while the flag fetch is in
  // flight and falls back to a `false` default if it fails, so both the
  // loading state and the error state land on phenotype-only ranking.
  const vaFlags = useVAFeatureFlags();
  const molecularBoostEnabled = vaFlags?.insightsMolecularBoost === true;
  // TGCLOUD-1794 Phase 3 — annotation only, off unless a deployment opts in.
  const genccGradesEnabled = vaFlags?.insightsGenccGrades === true;

  return (
    <Grid container spacing={1}>
      {localHasInputs ? (
        <ProfileMatchBox
          // Keyed on the PATIENT. The deferral above holds a ranking in state
          // while the section is collapsed, deliberately, so re-opening it for
          // the same person is instant. Across a person change that same state
          // is the previous patient's ranked diseases, and re-expanding painted
          // them under the new patient's name until the refetch landed. A key
          // discards them at the moment the person changes, which is the only
          // point at which they stop being true.
          //
          // Composite because neither id alone identifies every person: the
          // fetch above reads `proBandId ?? familyMemberId`, so a proband can
          // arrive carrying only the former. Keying on one of them would give
          // two different people the same key and defeat the whole thing.
          key={`${proBandId || ''}:${familyMemberId || data?.pid || ''}`}
          active={active}
          hpoIds={fetchData.hpoTerms}
          excludedHpoIds={fetchData.excludedHpoTerms}
          diseaseIds={diseaseIds}
          patientGeneSet={patientGeneSet}
          addSuggestion={addSuggestion}
          existingIds={existingIds}
          family={analysis?.family}
          patientPathogenicGenes={patientPathogenicGenes}
          molecularBoostEnabled={molecularBoostEnabled}
          genccGradesEnabled={genccGradesEnabled}
          enrichmentDegraded={enrichmentDegraded}
        />
      ) : fetchLoading ? (
        <Grid
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.8rem',
            bgcolor: 'rgba(46, 55, 71, 0.03)',
            border: '1px solid rgba(46, 55, 71, 0.08)',
            p: 1,
            borderRadius: 1
          }}
          size={12}
        >
          <CircularProgress size={14} />
          <span>Loading suggestions…</span>
        </Grid>
      ) : (
        <Grid
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.8rem',
            fontWeight: 'bold',
            bgcolor: '#fff4e6',
            p: 1,
            borderRadius: 1
          }}
          size={12}
        >
          <AutoAwesome fontSize="small" />
          <span>
            Add phenotypes and other clinical data to generate suggestions
          </span>
        </Grid>
      )}
    </Grid>
  );
}

export default Suggestions;

// TGCLOUD-1846 Phase 3 — why a recorded HPO code was left out of the ranking.
// The server resolves an old code to the term it now stands for; one it cannot
// resolve to a single current term is not scored (it would match nothing and
// dilute every score) and comes back in `inputs.unscored` with a reason.
const UNSCORED_REASONS = {
  'no-single-replacement': 'obsolete in HPO with no single replacement',
  'not-in-release': 'not in this HPO release',
  // The terminology service has not loaded HPO's old-code data yet, so it
  // cannot say where an old code went — which is not the same as "nowhere".
  'resolution-unavailable':
    'cannot be checked yet: HPO’s old-code data is not loaded (an admin can re-seed HPO)',
  'not-hpo': 'not an HPO code',
  'no-annotations': 'HPO has no annotations for this diagnosis'
};

export function describeUnscoredInput(u) {
  const label = u.name && u.name !== u.id ? `${u.name} (${u.id})` : u.id;
  const reason = UNSCORED_REASONS[u.reason] || 'could not be scored';
  const candidates = (u.candidates || []).map((c) => c.name).filter(Boolean);
  // A ruled-out finding that was not scored carries no penalty — say so, or
  // it reads exactly like a present one.
  const ruledOut = u.kind === 'excluded' ? ' (ruled out; not counted)' : '';
  return `${label}${ruledOut}: ${reason}${
    candidates.length ? ` — HPO suggests ${candidates.join(' or ')}` : ''
  }`;
}

// Profile-level match — ranks diseases/genes against the full HPO profile
// using Phenomizer-style semantic similarity (with Jaccard as a fallback).
// Mirrors hpo.jax.org's profile-search behaviour.
const ProfileMatchBox = ({
  active = true,
  hpoIds,
  excludedHpoIds,
  diseaseIds,
  patientGeneSet,
  addSuggestion,
  existingIds,
  family,
  patientPathogenicGenes,
  molecularBoostEnabled,
  genccGradesEnabled,
  enrichmentDegraded
}) => {
  const [results, setResults] = React.useState({
    diseases: [],
    genes: [],
    phenotypes: []
  });
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode] = React.useState(null);
  // CS-5 — null while the ranking is usable. Set to a { kind, message } when the
  // panel has NO ranking to show for a reason that is not a clinical finding, so
  // "the service is busy" can never render as "no matches".
  const [failure, setFailure] = React.useState(null);
  // Server-resolved metadata: the recorded inputs (named) and what the server
  // left out. Drives the caption denominator and the exclusion note.
  const [meta, setMeta] = React.useState(null);
  // Bumped by the manual refresh button to re-issue the ranking requests.
  //
  // CS-10: this is a RETRY, and nothing more. It does not clear any cache. The
  // comment here used to claim it was "useful after a terminology-service
  // re-seed invalidates the in-memory cache", which was backwards twice over —
  // a re-seed does not invalidate anything by itself, and the scoring cache
  // lives per WORKER THREAD inside the terminology service, so re-issuing the
  // same request just reaches the same stale worker and returns the same stale
  // answer. Clearing it is an operator action: POST /profile-search/reload on
  // the terminology service (deliberately not proxied to the browser — it
  // drains the worker pool for tens of seconds). The automatic bundle swap
  // already calls computePool.reloadAll() for itself.
  const [refreshTick, setRefreshTick] = React.useState(0);

  const totalInputs = (hpoIds?.length || 0) + (diseaseIds?.length || 0);

  // Cross-evidence: a family-level syndrome pattern (from the pure pedigree
  // analyzer) that corroborates a Profile Match disease/gene for this person.
  // Distinct from Phase A's "gene + phenotype" convergence — this is the
  // "family + variant" signal. Matched here (where `results` live) rather than
  // inside analyzePedigree, which stays pure and family-scoped.
  const crossEvidence = useMemo(() => {
    const empty = { diseases: new Set(), genes: new Set() };
    if (!family) return empty;
    const edges = detectCrossEvidence(
      { family },
      { diseases: results.diseases, genes: results.genes }
    );
    const out = { diseases: new Set(), genes: new Set() };
    for (const e of edges) {
      if (e.target.resultType === 'disease')
        out.diseases.add(e.target.resultId);
      else if (e.target.resultType === 'gene')
        out.genes.add(String(e.target.resultId).toUpperCase());
    }
    return out;
  }, [family, results]);

  // Phase C — molecular support: when the patient carries a pathogenic variant
  // in a gene linked to a ranked disease, boost that disease, weighted by the
  // family's inheritance pattern for it (dominant ⇒ strong, recessive ⇒
  // carrier-level). Re-sort diseases on the boosted score; the row enriches the
  // existing convergence chip rather than adding a new one.
  //
  // GATED, DEFAULT OFF — `insightsMolecularBoost` (TGCLOUD-1712). With the flag
  // off this returns the server's ordering byte-for-byte: no score arithmetic,
  // no re-sort, and no `_support` field, so the row's tooltip carries no
  // molecular-support phrase either. That is what makes the displayed ranking
  // independent of the patient's genetic test results. Anything added here that
  // reads genotype outside the `if` re-opens the defect.
  const rankedDiseases = useMemo(() => {
    if (!molecularBoostEnabled) return results.diseases || [];
    const inheritanceByDisease = family?.inheritanceByDisease || {};
    return (results.diseases || [])
      .map((d) => {
        const linkedPathogenic = (d.associatedGenes || []).filter((g) =>
          patientPathogenicGenes?.has?.(String(g).toUpperCase())
        );
        const support = molecularSupport({
          holdsPathogenicLinkedGene: linkedPathogenic.length > 0,
          inheritancePattern: inheritanceByDisease[d.id]
        });
        // CS-3 — boost-only, and only from zero up. A multiplicative boost on a
        // NEGATIVE score makes it MORE negative, so molecular support used to
        // push a phenotype-penalised disease further down the list: exactly
        // backwards. A demoted disease is left where the phenotype evidence put
        // it; molecular support never rescues it and never worsens it.
        const own = d.score || 0;
        return {
          ...d,
          score: own > 0 ? own * (1 + support.boost) : own,
          _support: support,
          _supportGenes: linkedPathogenic
        };
      })
      .sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [results.diseases, patientPathogenicGenes, family, molecularBoostEnabled]);

  // GenCC validity for the genes on screen (TGCLOUD-1794 Phase 3).
  //
  // Fetched CLIENT-SIDE and OFF by default. Asking /profile-search to attach
  // grades would mean sending the patient's genes to the terminology service,
  // and Clinical Insights ranking is deliberately phenotype-only —
  // Suggestions.test.jsx pins that no gene symbol or id appears in a
  // profile-search request. The annotation is an overlay on the ranking, so it
  // is fetched separately.
  //
  // With the flag off nothing is requested and `genccBatch` stays null, so the
  // rows render exactly as they do today.
  const genccEnabled = genccGradesEnabled === true;
  const genesForGencc = React.useMemo(
    () =>
      genccEnabled
        ? genesToGrade({
            diseaseRows: results.diseases || [],
            geneRows: results.genes || [],
            patientGeneSet
          })
        : [],
    [genccEnabled, results.diseases, results.genes, patientGeneSet]
  );
  const genccBatch = useGenccGrades(genesForGencc, { enabled: genccEnabled });

  React.useEffect(() => {
    // Deferred until the section is opened. Nothing is reset while closed: a
    // collapsed section must not discard a ranking the clinician has already
    // seen, and the collapsed summary line reads its counts from the FIRST
    // request wave, not from this one, so it is unaffected either way.
    if (!active) return;
    if (totalInputs === 0) {
      setResults({ diseases: [], genes: [], phenotypes: [] });
      setFailure(null);
      setMeta(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailure(null);
    const basePayload = {
      hpoIds,
      excludedHpoIds: excludedHpoIds || [],
      diseaseIds,
      limit: 10
    };
    Promise.all([
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'disease'
      }),
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'gene'
      }),
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'phenotype'
      })
    ])
      .then(([d, g, p]) => {
        setResults({
          diseases: d.data?.results || [],
          genes: g.data?.results || [],
          phenotypes: p.data?.results || []
        });
        setMode(d.data?.mode || null);
        setMeta({
          inputs: d.data?.inputs || null,
          suppressedAsRecorded: d.data?.suppressedAsRecorded || [],
          // The HPO release the ranking was computed from — what the scoring
          // cache was built on, not what is loaded now (TGCLOUD-1846).
          hpoRelease: d.data?.hpoRelease || null
        });
        // CS-5, second face of the same defect. A 2xx with `mode: null` is the
        // backend's "HPO is switched off in Settings → Terminology" refusal
        // (backend/routes/terminology.js short-circuits with
        // `{ results: [], mode: null }`); every real ranking carries a mode
        // string. Rendering that as "No matches" told the clinician the patient
        // has no candidate diagnoses when in fact nothing was searched.
        if (!d.data?.mode) {
          setFailure({
            kind: 'unavailable',
            message:
              'Ranking not available — the terminology service returned no ranking. HPO may be disabled in Settings → Terminology.'
          });
        }
      })
      .catch((err) => {
        setResults({ diseases: [], genes: [], phenotypes: [] });
        setMeta(null);
        // CS-5 — a failed request is NOT a negative finding. Every branch here
        // must leave `failure` set, because the alternative rendering ("No
        // matches") is a clinical statement about the patient.
        const status = err?.response?.status;
        setFailure(
          status === 503
            ? {
                kind: 'busy',
                message:
                  'Ranking temporarily unavailable — the matching service is busy. This is not a result: nothing has been ruled out. Retry in a few seconds.'
              }
            : {
                kind: 'error',
                message:
                  'Ranking could not be completed, so no candidates are shown. This is a service error, not a clinical finding — nothing has been ruled out.'
              }
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    hpoIds.join(','),
    (excludedHpoIds || []).join(','),
    (diseaseIds || []).join(','),
    refreshTick
  ]);

  const hpoCount = hpoIds?.length || 0;
  const excludedCount = excludedHpoIds?.length || 0;
  const dxCount = diseaseIds?.length || 0;
  // Terms the recorded diagnoses contributed on top of the recorded
  // phenotypes. The expanded query is (phenotypes ∪ every HPO annotation of
  // every recorded disease), and the phenotypes come first, so the difference
  // against the phenotype count IS the inherited set. Zero when no disease is
  // recorded, or when the server sent no input summary.
  // Counted as the server scored them: a recorded code that was not scored
  // adds nothing, and an old code scored as a term also recorded under its
  // current code is one term, not two (TGCLOUD-1846).
  const scoredPhenotypeTerms = new Set(
    (meta?.inputs?.phenotypes || [])
      .filter((p) => p.scored !== false)
      .map((p) => p.scoredAs?.id || p.id)
  ).size;
  const expandedExtra = meta?.inputs?.expandedTerms
    ? Math.max(0, meta.inputs.expandedTerms - scoredPhenotypeTerms)
    : 0;
  const headerSummary = [
    hpoCount > 0 && `${hpoCount} phenotype${hpoCount === 1 ? '' : 's'}`,
    excludedCount > 0 && `${excludedCount} ruled out`,
    dxCount > 0 && `${dxCount} disease${dxCount === 1 ? '' : 's'}`
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Grid size={12}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          bgcolor: 'rgba(46, 55, 71, 0.03)',
          p: 1,
          borderRadius: 1,
          flexDirection: 'column',
          border: '1px solid rgba(46, 55, 71, 0.08)'
        }}
      >
        <Box
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.85rem',
            fontWeight: 'bold'
          }}
        >
          <AutoAwesome fontSize="small" />
          <span>Profile Match</span>
          <Box
            sx={{
              fontSize: '.7rem',
              fontWeight: 400,
              color: 'text.secondary',
              flex: 1
            }}
          >
            ranked across {headerSummary}
            {mode ? ` · ${mode}` : ''}
            {/* D5 — name the expansion instead of letting it leak out as an
                inflated caption numerator. A recorded disease contributes all
                of its HPO annotations to the comparison, which is why the
                number of terms compared exceeds the number of findings
                recorded. */}
            {expandedExtra > 0 && (
              <Tooltip
                title={`${meta.inputs.expandedTerms} HPO terms were compared: the recorded phenotypes plus ${expandedExtra} inherited from the recorded diagnoses. Each match is scored per recorded finding — open "Why this match?" on a row to see which.`}
              >
                <span
                  style={{ textDecoration: 'underline dotted', marginLeft: 4 }}
                >
                  {`+${expandedExtra} inherited terms`}
                </span>
              </Tooltip>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
            title="Refresh profile match"
            sx={{ p: 0.25 }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>

        {/* CS-6 — the ranking is complete (no phenotype was dropped); the term
            DESCRIPTIONS are stale. Said plainly, because the previous behaviour
            was to drop the term and say nothing. */}
        {enrichmentDegraded && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'flex-start',
              px: 1,
              fontSize: '.72rem',
              color: '#8d6e00'
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 14, mt: '1px' }} />
            <span>
              {`HPO term details unavailable for ${enrichmentDegraded.failed} of ${enrichmentDegraded.total} phenotypes — all are still included in the ranking, shown with their stored names.`}
            </span>
          </Box>
        )}

        {/* TGCLOUD-1846 Phase 3 — a recorded code left out of the ranking is
            named here, so it never reads as a finding with no overlap. */}
        {!loading && meta?.inputs?.unscored?.length > 0 && (
          <Box
            data-testid="unscored-inputs"
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'flex-start',
              px: 1,
              fontSize: '.72rem',
              color: '#8d6e00'
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 14, mt: '1px' }} />
            <span>
              {`Not in the ranking — ${meta.inputs.unscored
                .map(describeUnscoredInput)
                .join('; ')}.`}
            </span>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        ) : failure ? (
          // CS-5 — the whole panel, not a per-list "No matches". There is
          // nothing to say about diseases, genes or phenotypes because nothing
          // was ranked, and saying it three times as an empty result would be
          // three false negatives.
          <Box
            role="alert"
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              bgcolor: '#fff4e6',
              border: '1px solid #ffb74d',
              borderRadius: 1,
              p: 1,
              fontSize: '.78rem'
            }}
          >
            <WarningAmberRounded fontSize="small" sx={{ color: '#e65100' }} />
            <Box sx={{ flex: 1 }}>{failure.message}</Box>
          </Box>
        ) : (
          <>
            {meta?.suppressedAsRecorded?.length > 0 && (
              <Box
                sx={{
                  px: 1,
                  fontSize: '.72rem',
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                {`Already on the record, so not ranked: ${meta.suppressedAsRecorded
                  .map((s) => s.name)
                  .filter((n, i, a) => a.indexOf(n) === i)
                  .join(', ')}`}
              </Box>
            )}
            <ProfileMatchList
              title="Diseases"
              items={rankedDiseases}
              type="Diseases"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Diseases}
              patientGeneSet={patientGeneSet}
              crossEvidenceIds={crossEvidence.diseases}
              genccBatch={genccEnabled ? genccBatch : null}
            />
            <ProfileMatchList
              title="Genes"
              items={results.genes}
              type="Genes"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Genes}
              crossEvidenceIds={crossEvidence.genes}
              genccBatch={genccEnabled ? genccBatch : null}
            />
            <ProfileMatchList
              title="Phenotypes to check"
              items={results.phenotypes}
              type="Phenotypes"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Phenotypes}
            />
            {/* HPO licence: the release behind this ranking (TGCLOUD-1846). */}
            <HpoReleaseNote release={meta?.hpoRelease} />
          </>
        )}
      </Box>
    </Grid>
  );
};

const ProfileMatchList = ({
  title,
  items,
  type,
  addSuggestion,
  totalInputs,
  existingIds,
  patientGeneSet,
  crossEvidenceIds,
  genccBatch
}) => {
  // Pick the colour family matching how these entities are stored in the
  // patient record — Diseases → disease tile palette, Genes → genotype,
  // Phenotypes → phenotype tile palette.
  const palette =
    type === 'Diseases'
      ? TILE_COLOURS.disease
      : type === 'Phenotypes'
      ? TILE_COLOURS.phenotype
      : TILE_COLOURS.genotype;
  if (!items || items.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ px: 1, fontSize: '.8rem', fontWeight: 'bold' }}>{title}</Box>
        <Box sx={{ px: 1, fontSize: '.75rem', color: 'text.secondary' }}>
          No matches
        </Box>
      </Box>
    );
  }
  // Normalise to 0..1 across the displayed set so the bar widths are
  // comparable. The absolute score's units depend on mode, but relative
  // ranking is what the clinician cares about.
  //
  // CS-3/CS-4 — scores are deliberately un-clamped server-side and CAN be
  // negative (absent-phenotype penalty, NOT-qualified contradiction). Two ways
  // that used to corrupt the bar:
  //   * all scores negative → Math.max is negative → dividing one negative by
  //     another INVERTS the scale, so the WORST match drew the longest bar;
  //   * one negative score → `width: -12.5%` is invalid CSS, the declaration is
  //     dropped, and with `inset: 0` still in force left AND right anchor to 0 —
  //     a heavily-penalised disease rendered a FULL-WIDTH strength bar.
  // Fixed at the source of both: normalise only against a positive maximum, and
  // clamp every width into [0, 100].
  const positiveMax = Math.max(0, ...items.map((x) => Number(x.score) || 0));
  const maxScore = positiveMax > 0 ? positiveMax : null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ px: 1, fontSize: '.8rem', fontWeight: 'bold' }}>{title}</Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        {items.map((res) => (
          <ProfileMatchRow
            key={res.id}
            res={res}
            type={type}
            relScore={maxScore ? (Number(res.score) || 0) / maxScore : 0}
            addSuggestion={addSuggestion}
            palette={palette}
            totalInputs={totalInputs}
            existing={!!existingIds && existingIds.has(res.id)}
            patientGeneSet={patientGeneSet}
            crossEvidenceIds={crossEvidenceIds}
            genccBatch={genccBatch}
          />
        ))}
      </Box>
    </Box>
  );
};

// GenCC validity for the gene-disease links behind one Clinical Insights row.
// TGCLOUD-1794 Phase 3 — annotation only, behind insightsGenccGrades.
//
// A disease row grades the genes the PATIENT holds against THIS disease; a gene
// row grades that gene against its own diseases. Every submitter is shown and
// none is collapsed — 39.5% of pairs disagree.
//
// The grade is always the WORD. Colour would not survive the monochrome print
// these panels are read from, and would not survive a colour-blind reader
// either.
const GenccRowGrades = ({ res, type, heldGenes, batch, palette }) => {
  const isDisease = type === 'Diseases';
  const genes = isDisease
    ? heldGenes
    : [
        String(res.name || res.id || '')
          .split(' (')[0]
          .trim()
      ].filter(Boolean);
  if (!genes.length) return null;

  const graded = genes
    .map((g) => pairGrade(batch, g, isDisease ? res.id : null))
    .filter(Boolean);
  if (!graded.length) return null;

  // Nothing to say is better than a row of empty labels: a gene with no
  // submission for THIS disease is reported once, quietly, not per gene.
  const informative = graded.filter(
    (g) =>
      g.state === GENE_VALIDITY_STATE.GRADED ||
      g.state === GENE_VALIDITY_STATE.CONFLICTING
  );
  const unidentified = graded.filter(
    (g) => g.state === GENE_VALIDITY_STATE.UNIDENTIFIED_GENE
  );

  if (!informative.length) {
    return (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.25,
          color: palette.subtext,
          fontSize: '.72rem'
        }}
      >
        {unidentified.length
          ? 'GenCC: gene not identified — validity could not be looked up'
          : 'GenCC: no submission for this gene\u2013disease link'}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 0.25, display: 'grid', gap: 0.15 }}>
      {informative.map((g) => (
        <Typography
          key={g.gene}
          variant="caption"
          sx={{ color: palette.subtext, fontSize: '.72rem' }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            GenCC {g.gene}:
          </Box>{' '}
          {[...new Set(g.submitters.map((s) => s.classification))].join(', ')}
          {g.conflicting && (
            <Box component="span" sx={{ fontWeight: 600, color: '#8d5700' }}>
              {' \u00b7 submitters disagree'}
            </Box>
          )}
          {batch.datasetVersion ? ` \u00b7 GenCC ${batch.datasetVersion}` : ''}
        </Typography>
      ))}
    </Box>
  );
};

const ProfileMatchRow = ({
  res,
  type,
  relScore,
  addSuggestion,
  palette,
  totalInputs,
  existing,
  patientGeneSet,
  crossEvidenceIds,
  genccBatch
}) => {
  const [loading, setLoading] = React.useState(false);
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  const onAdd = async () => {
    if (existing) return;
    setLoading(true);
    await addSuggestion(type, { id: res.id, name: res.name });
    setLoading(false);
  };
  // CS-3/CS-4 — a demoted row can never draw a strong bar. Clamped into
  // [0, 100] so the width is always valid CSS, and 0 for anything at or below
  // zero: a negative score is a demotion, not a weak positive.
  const pct = Math.min(
    100,
    Math.max(0, Math.round((Number(relScore) || 0) * 100))
  );

  // CS-4 — the penalty, made visible. `penalty` is the absent-phenotype
  // deduction (the patient explicitly does NOT have a finding this entity is
  // annotated with); `contradiction` is TGCLOUD-1715's NOT-qualified evidence
  // against (the patient HAS a finding this disease is documented not to
  // present with). Either one silently pushed a row down the list with no
  // indication whatsoever.
  const ruledOutHits = Array.isArray(res.excludedMatchDetails)
    ? res.excludedMatchDetails
    : (res.excludedMatches || []).map((id) => ({ id, name: id }));
  const contradicted = Array.isArray(res.contradictedTermDetails)
    ? res.contradictedTermDetails
    : (res.contradictedTerms || []).map((id) => ({ id, name: id }));
  const demoted = (res.penalty || 0) > 0 || (res.contradiction || 0) > 0;
  const negative = (Number(res.score) || 0) <= 0;

  // D5 — per-recorded-input breakdown from the server. One entry per input the
  // clinician actually recorded, which is the same universe as the denominator.
  const inputSupport = Array.isArray(res.inputSupport) ? res.inputSupport : [];
  const supportingCount = Number.isFinite(res.supportingInputCount)
    ? res.supportingInputCount
    : inputSupport.filter((s) => s.supports).length;
  const inputDenominator = Number.isFinite(res.inputCount)
    ? res.inputCount
    : totalInputs;
  const hasBreakdown = inputSupport.length > 0;
  // Candidate genes for a disease (gene↔disease associations from the
  // terminology service) + convergence: which of those the patient holds.
  const associatedGenes =
    type === 'Diseases' && Array.isArray(res.associatedGenes)
      ? res.associatedGenes
      : [];
  const heldGenes = associatedGenes.filter((g) =>
    patientGeneSet?.has?.(String(g).toUpperCase())
  );
  // Gene rows match the family-syndrome gene by symbol ("BRCA1"); disease rows
  // by id ("OMIM:114480").
  const crossKey =
    type === 'Genes'
      ? (res.name || res.id || '').split(' (')[0].trim().toUpperCase()
      : res.id;
  const hasCrossEvidence = !!crossEvidenceIds && crossEvidenceIds.has(crossKey);
  // Phase C molecular-support phrase, weighted by the family inheritance
  // pattern — enriches the convergence chip tooltip (no separate chip).
  const support = res._support;
  const supportText = support?.level
    ? support.basis === 'recessive_carrier'
      ? 'recessive inheritance — carrier-level support only'
      : support.basis === 'dominant'
      ? `${support.level} molecular support (dominant inheritance)`
      : `${support.level} molecular support`
    : null;
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: palette.background,
        border: `1px solid ${palette.border}`,
        borderLeft: `3px solid ${palette.accent}`,
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        cursor: existing ? 'default' : 'pointer',
        overflow: 'hidden',
        '&:hover': existing ? undefined : { filter: 'brightness(0.97)' }
      }}
      onClick={onAdd}
    >
      <Box
        data-testid={`match-bar-${res.id}`}
        data-pct={pct}
        sx={{
          position: 'absolute',
          // NOT `inset: 0`. With inset, `left` and `right` are both 0, so if the
          // width declaration is ever dropped the overlay spans the full row —
          // which is how a negative score used to render a full strength bar
          // (CS-4). Anchoring left/top/bottom only means a missing width
          // collapses to nothing, not to everything.
          left: 0,
          top: 0,
          bottom: 0,
          width: `${pct}%`,
          // Subtle score-bar overlay tinted with the palette's accent.
          bgcolor: palette.accent,
          opacity: 0.08,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: palette.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {res.name}
          </Typography>
          <Typography variant="body2" sx={{ color: palette.subtext }}>
            {res.id}
            {(() => {
              // Phenotype-kind rows have no per-input breakdown (they're
              // frequency × IC aggregations). totalAnnotations carries the
              // supporting disease count — show "supported by N records".
              if (type === 'Phenotypes') {
                const n = res.totalAnnotations || 0;
                return ` · supported by ${n} record${n === 1 ? '' : 's'}`;
              }
              // D5 — REPLACES "17/6 inputs related".
              //
              // The old caption divided a count of EXPANDED query terms (one per
              // HPO annotation of every recorded disease, unioned with the
              // patient's phenotypes) by a count of RECORDED inputs. Two
              // different universes, so the numerator could exceed the
              // denominator, and neither number was something a clinician could
              // act on. Both numbers now come from the server's per-input
              // breakdown, so they are the same universe by construction: the
              // things the clinician put on the record.
              if (hasBreakdown) {
                return ` · ${supportingCount} of ${inputDenominator} recorded finding${
                  inputDenominator === 1 ? '' : 's'
                } support this`;
              }
              if (Number.isFinite(res.matchedTerms?.length)) {
                return ` · ${res.matchedTerms.length} exact match${
                  res.matchedTerms.length === 1 ? '' : 'es'
                }`;
              }
              return '';
            })()}
            {res.alternateCodings?.length > 0 && (
              <Tooltip
                title={`Also coded as ${res.alternateCodings
                  .map((a) => a.id)
                  .join(
                    ', '
                  )} — the same disease in another vocabulary, shown once`}
              >
                <span>{` · +${res.alternateCodings.length} coding${
                  res.alternateCodings.length === 1 ? '' : 's'
                }`}</span>
              </Tooltip>
            )}
          </Typography>

          {/* CS-4 — a demotion the clinician can see and check. Both causes are
              curated negatives, so each names the finding responsible. */}
          {demoted && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                mt: 0.25,
                color: '#b71c1c',
                fontSize: '.74rem'
              }}
            >
              <ReportProblemOutlined sx={{ fontSize: 14, mt: '1px' }} />
              <span>
                {[
                  ruledOutHits.length > 0 &&
                    `Ranked down: annotated with ${ruledOutHits
                      .map((t) => t.name)
                      .join(', ')}, ruled out for this patient`,
                  contradicted.length > 0 &&
                    `Ranked down: documented NOT to present with ${contradicted
                      .map((t) => t.name)
                      .join(', ')}`,
                  negative && 'evidence overall is against this candidate'
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </Box>
          )}

          {hasBreakdown && (
            <Box sx={{ mt: 0.25 }}>
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBreakdown((v) => !v);
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  border: 0,
                  p: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: palette.subtext,
                  font: 'inherit',
                  fontSize: '.74rem',
                  textDecoration: 'underline'
                }}
              >
                {showBreakdown ? 'Hide match detail' : 'Why this match?'}
                {showBreakdown ? (
                  <ExpandLess sx={{ fontSize: 14 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 14 }} />
                )}
              </Box>
              <Collapse in={showBreakdown} unmountOnExit>
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    m: 0,
                    mt: 0.25,
                    pl: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    fontSize: '.74rem',
                    color: palette.subtext
                  }}
                >
                  {inputSupport.map((s) => (
                    <Box component="li" key={`${s.kind}-${s.id}`}>
                      {s.scored === false
                        ? `${s.name}${
                            s.kind === 'disease' ? ' (recorded diagnosis)' : ''
                          } → not scored (${
                            UNSCORED_REASONS[s.reason] || 'could not be scored'
                          })`
                        : s.supports
                        ? // The strength word is the server's, derived from the
                          // ontology's own IC scale (CS-9) — the UI must not
                          // invent a band of its own.
                          s.kind === 'disease'
                          ? `${s.name} (recorded diagnosis) → ${
                              s.via?.name || 'a shared feature'
                            } matches ${s.matched?.name || 'an annotation'} · ${
                              s.strength
                            }`
                          : `${s.name} → ${
                              s.matched?.name || 'an annotation'
                            } · ${s.strength}`
                        : `${s.name}${
                            s.kind === 'disease' ? ' (recorded diagnosis)' : ''
                          } → no meaningful overlap`}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
          {hasCrossEvidence && (
            <Box sx={{ mt: 0.25 }}>
              <Tooltip title="A family-level syndrome pattern in the pedigree corroborates this result">
                <Chip
                  label="family + variant"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '.72rem',
                    textTransform: 'capitalize',
                    bgcolor: '#7b1fa2',
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              </Tooltip>
            </Box>
          )}
          {associatedGenes.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: 0.25
              }}
            >
              {heldGenes.length > 0 && (
                <Tooltip
                  title={`Patient carries ${heldGenes.join(
                    ', '
                  )} — molecular and phenotype evidence converge${
                    supportText ? ` · ${supportText}` : ''
                  }`}
                >
                  <Chip
                    label="gene + phenotype"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '.72rem',
                      textTransform: 'capitalize',
                      bgcolor: palette.accent,
                      color: '#fff',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                </Tooltip>
              )}
              <Typography
                variant="caption"
                sx={{ color: palette.subtext, fontSize: '.78rem' }}
              >
                Genes: {associatedGenes.slice(0, 6).join(', ')}
                {associatedGenes.length > 6 ? '…' : ''}
              </Typography>
            </Box>
          )}
          {/* GenCC curated validity for the gene-disease links behind this row
              (TGCLOUD-1794 Phase 3). ANNOTATION ONLY — nothing here is read by
              the sort, the filter or the membership of the list. Rendered only
              when the deployment has switched it on. */}
          {genccBatch && (
            <GenccRowGrades
              res={res}
              type={type}
              heldGenes={heldGenes}
              batch={genccBatch}
              palette={palette}
            />
          )}
        </Box>
        {loading ? (
          <CircularProgress size={14} />
        ) : existing ? (
          <BookmarkRounded
            fontSize="small"
            titleAccess="Already in patient record"
            sx={{ color: palette.accent }}
          />
        ) : (
          <AddCircleRounded fontSize="small" sx={{ color: palette.accent }} />
        )}
      </Box>
    </Box>
  );
};
